import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSortUp, faSortDown } from "@fortawesome/free-solid-svg-icons";
import { useClassNames } from "../../../hooks/useClassNames";
import { TableColumn } from "./types";
import { Skeleton } from "@/components/shadcn/skeleton";

export type DataTableProps<T extends { id?: string | number }, TSortKey = never> = {
  columns: TableColumn<T, TSortKey>[],
  data: T[],
  sortColumn?: TSortKey,
  sortAsc?: boolean,
  onSort?: (newSortColumn: TSortKey, newSortAsc: boolean) => void,
  isLoading?: boolean,
  skeletonRows?: number
};

export const DataTable = <T extends { id?: string | number }, TSortKey = never>({ columns, data, sortColumn, sortAsc = true, onSort, isLoading = false, skeletonRows = 5 }: DataTableProps<T, TSortKey>) => {
  const handleSort = (newSortColumn?: TSortKey) => {
    if (newSortColumn === undefined || newSortColumn === null || !onSort) {
      return;
    }

    if (sortColumn === newSortColumn) {
      onSort(sortColumn, !sortAsc);
    } else {
      onSort(newSortColumn, true);
    }
  };

  const renderSkeletonCell = (column: TableColumn<T, TSortKey>) => {
    const config = column.skeleton || {};
    let skeletonClass: string;

    if (config.variant === 'short') {
      skeletonClass = "h-6 w-14";
    } else if (config.variant === 'long') {
      skeletonClass = "h-6 w-48";
    } else {
      skeletonClass = "h-6 w-24";
    }

    return <Skeleton className={skeletonClass} />;
  };

  const renderTableData = () => {
    if (isLoading) {
      // Render skeleton rows
      return Array.from({ length: skeletonRows }, (_, rowIndex) => (
        <tr key={`skeleton-${rowIndex}`} className="border-b border-gray-300">
          {columns.filter(col => !col.hidden).map((col) => (
            <td 
              key={col.key} 
              className={useClassNames(
                "align-middle px-4 py-2",
                col.isAction && "text-right whitespace-nowrap",
                col.className
              )}
            >
              {renderSkeletonCell(col)}
            </td>
          ))}
        </tr>
      ));
    }

    return data.map((item, idx) => (
      <tr
        key={item.id ?? idx}
        className="border-b border-gray-300"
      >
        {columns.filter(col => !col.hidden).map((col) => (
          <td 
            key={col.key} 
            className={useClassNames(
              "align-middle px-4 py-2",
              col.isAction && "text-right whitespace-nowrap",
              col.className
            )}
          >
            {col.render(item)}
          </td>
        ))}
      </tr>
    ));
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="h-[50px] text-base border-t border-b border-table-border">
          <tr>
            {columns.filter(col => !col.hidden).map((col) => (
              <th
                key={col.key}
                className={useClassNames(
                  "align-middle content-center bg-table-header text-table-header-foreground font-semibold px-4 py-3 select-none text-left",
                  col.sortKey !== undefined && "cursor-pointer",
                  col.isAction && "w-2 text-right",
                  col.className
                )}
                onClick={col.sortKey !== undefined ? () => handleSort(col.sortKey) : undefined}
              >
                {col.isAction ? null : (
                  <span className="inline-flex items-center gap-1">
                    {col.header}
                    {col.sortKey !== undefined && sortColumn === col.sortKey && (
                      <FontAwesomeIcon
                        icon={sortAsc ? faSortUp : faSortDown}
                        className={`relative ${sortAsc ? 'top-[4px]' : 'top-[-3px]'}`}
                      />
                    )}
                  </span>
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {renderTableData()}
        </tbody>
      </table>
    </div>
  );
};
